import styled from '@emotion/styled';
import { Variables } from '../../base';
import { Toggle as BaseToggle } from '../../base/toggle/toggle';
import { dayPickerBaseStyles } from '../../base/datepicker/datepicker.styles';
export const DropdownTrigger = styled.div<{ disabled?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 36px;
    width: 128px;
    padding: 6px 32px 6px 4px;
    cursor: ${p => (p.disabled ? 'default' : 'pointer')};
    font-size: 14px;
    color: ${Variables.ThemeLight.TextMedium};

    &:hover {
        border-color: ${p => (p.disabled ? Variables.ThemeLight.Border3 : Variables.Colors.n30)};
    }

    &:focus-within {
        outline: 2px solid ${Variables.Colors.b300};
        outline-offset: 2px;
    }
`;

export const DropdownTriggerValue = styled.span`
    flex: 1;
    text-align: left;
    white-space: nowrap;
`;

export const DropdownTriggerPlaceholder = styled.span`
    flex: 1;
    text-align: left;
    color: ${Variables.Colors.n50};
`;

export const TagsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    min-height: 36px;
    padding: 4px 0;
    max-width: 165px;
`;

export const TagsList = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    flex: 1 1 auto;
`;

export const TagsFilterTrigger = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

export const TagPill = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: ${Variables.Rounded.xlarge};
    font-size: 14px;
    font-weight: ${Variables.FontWeight.medium};
    color: ${Variables.ThemeLight.PrimaryInverted2};
    background-color: ${Variables.ThemeLight.PrimaryInverted9};
`;

export const AddTagButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px dashed ${Variables.Colors.n30};
    border-radius: ${Variables.Rounded.medium};
    background: transparent;
    cursor: pointer;
    color: ${Variables.Colors.n70};
    transition: ${Variables.transition};

    &:hover {
        border-color: ${Variables.Colors.b300};
        color: ${Variables.Colors.b300};
    }
`;

export const AddIconWrapper = styled.span`
    display: flex;
    width: 18px;
    height: 18px;
    align-items: center;
    justify-content: center;
    svg {
        width: 18px;
        height: 18px;
    }
`;

export const UsersRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 36px;
`;

export const AssignToAddButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 1px dashed ${Variables.Colors.n30};
    border-radius: 50%;
    background: transparent;
    cursor: pointer;
    color: ${Variables.Colors.n70};
    flex-shrink: 0;
    transition: ${Variables.transition};

    &:hover {
        border-color: ${Variables.Colors.b300};
        color: ${Variables.Colors.b300};
    }
`;

export const PopoverOptionsList = styled.div`
    padding: 8px 0;
    min-width: 200px;
    max-height: 280px;
    overflow-y: auto;
`;


export const PopoverOption = styled.div<{ selected?: boolean }>`
    padding: 4px;
    border-radius: ${Variables.Rounded.xsmall};
    min-height: 24px;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 4px;
    color: ${Variables.ThemeLight.TextMedium};
    cursor: pointer;

    ${p =>
        p.selected &&
        `
        color: ${Variables.ThemeLight.PrimaryInverted2};
        font-weight: 500;
        background-color: inherit;
    `}

    &:hover {
        background-color:  ${Variables.ThemeLight.PrimaryMild3};
    }
    &:active {
        background-color: ${Variables.ThemeLight.PrimaryMedium3};
    }
`;

export const PopoverOptionIcon = styled.div`
    width: 14.4px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${Variables.ThemeLight.PrimaryConstant6};
`;

export const FieldsGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const FieldRow = styled.div`
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 24px;
    margin: 0;
    padding: 0;
`;

export const InlineField = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    margin: 0;
    padding: 0;
`;

export const LabeledField = styled.div<{ gap?: number }>`
    display: flex;
    flex-direction: column;
    gap: ${p => p.gap ?? 8}px;
    min-width: 0;
`;

export const FieldLabelTop = styled.label`
    font-size: 14px;
    font-weight: ${Variables.FontWeight.medium};
    color: ${Variables.ThemeLight.TextMedium};
    letter-spacing: 0.5px;
`;

export const ToggleLabelInline = styled.span`
    font-size: 14px;
    font-weight: ${Variables.FontWeight.medium};
    color: ${Variables.Colors.n85};
`;

export const PriorityTriggerWrapper = styled.div`
    display: inline-flex;
    align-items: center;
    width: 128px;
    gap: 6px;
    cursor: pointer;
`;

/** Priority pill: same colors and icon styling as messages table Priority column (message-list.styles.ts PriorityTag) */
interface IPriorityPillProps {
    priority: string; // '' | 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'
}

export const PriorityPill = styled.span<IPriorityPillProps>`
    display: inline-flex;
    align-items: center;
    height: 20px;
    font-size: 14px;
    padding: 4px 10px;
    box-sizing: border-box;
    font-weight: ${Variables.FontWeight.medium};
    border-radius: 9999px;
    line-height: 1;

    ${props => {
        const p = props.priority?.toUpperCase();
        switch (p) {
            case 'URGENT':
                return `
                    background: ${Variables.ThemeLight.WarningMild1};
                    color: ${Variables.ThemeLight.WarningInverted2};
                    svg path { fill: ${Variables.ThemeLight.WarningInverted3}; }
                `;
            case 'HIGH':
                return `
                    background: ${Variables.ThemeLight.AttentionMild1};
                    color: ${Variables.ThemeLight.AttentionInverted2};
                    svg path { fill: ${Variables.ThemeLight.AttentionInverted3}; }
                `;
            case 'NORMAL':
                return `
                    background: ${Variables.ThemeLight.PrimaryInverted9};
                    color: ${Variables.ThemeLight.PrimaryInverted2};
                    svg path { fill: ${Variables.ThemeLight.PrimaryInverted3}; }
                `;
            case 'LOW':
                return `
                    background: ${Variables.ThemeLight.SuccessMild1};
                    color: ${Variables.ThemeLight.SuccessInverted2};
                    svg path { fill: ${Variables.ThemeLight.SuccessInverted3}; }
                `;
            default:
                return `
                    background: ${Variables.ThemeLight.NeutralMild1};
                    color: ${Variables.ThemeLight.TextMedium};
                    svg path { fill: ${Variables.ThemeLight.TextMild}; }
                `;
        }
    }}
`;

export const PriorityPillIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
`;

export const InlineDropdownIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${Variables.Colors.n50};
    padding: 4px;

    &:hover {
        color: ${Variables.Colors.n70};
    }
    svg {
        width: 18px;
        height: 18px;
    }
`;

/** Figma 1918-41563: empty state – grey plus + label (e.g. "+ Due Date", "+ Priority"), bubble-like with transparent bg */
export const EmptyFieldTrigger = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    min-width: 32px;
    width: 128px;
    height: 32px;
    gap: 8px;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    font-weight: ${Variables.FontWeight.medium};
    color: ${Variables.ThemeLight.TextMedium};
    transition:
        background-color 0.2s ease,
        color 0.2s ease;

    &:hover {
        background: ${Variables.ThemeLight.NeutralOpacity3};
        color: ${Variables.ThemeLight.TextMedium};
    }
    &:active {
        background: ${Variables.ThemeLight.NeutralOpacity5};
        color: ${Variables.ThemeLight.PrimaryInverted1};
    }
    svg {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
        path {
            fill: currentColor;
        }
    }
`;

export const DatePopoverContainer = styled.div`
    ${dayPickerBaseStyles}

    .rdp-months {
        justify-content: center;
    }
`;

export const MessageFieldsToggle = styled(BaseToggle)`
    --toggle-bgcolor-on: ${Variables.Colors.b200};
`;

export const ToggleWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 64px;
`;